import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { FloatingLabel, Form, Row, Button as BSButton, Col } from "react-bootstrap";
import { ChargeCalculation, ChargeType, ServiceLocationModel, ServiceModel } from "../../../data/entities";
import { SlideOutModal, SlideOutModalProps, Button, SearchableChargeCalcDropdown, HandleDropDownListChange, SearchableChargeTypeDropdown, FormSectionDivider, FormSectionHeader, FormToggleLabel } from "../../../components";
import { useTenantFeatures } from "../../../hooks";
import { formatPrice } from "../../../utils";

export interface EditServiceLocationModalProps extends SlideOutModalProps {
  dataItem: ServiceLocationModel;
  service: ServiceModel;
  onSubmit: (serviceLocation: ServiceLocationModel) => void;
}
interface EditServiceFormValues {
  overridePrice: boolean;
  overrideChargeSettings: boolean;
  price: number | null;
  chargeType?: ChargeType | null;
  chargeCalculation?: ChargeCalculation | null;
  chargeAmount: number | null | undefined;
  serviceId: number;
}

export function EditServiceLocationModal(
  props: Readonly<EditServiceLocationModalProps>
) {
  const submitRef = useRef<HTMLButtonElement>(null);
  const { managePricing } = useTenantFeatures();
  const [overridePrice, setOverridePrice] = useState<boolean>(
    props.dataItem.price || props.dataItem.price === 0 ? true : false
  );
  const [overrideChargeSettings, setOverrideChargeSettings] =
    useState<boolean>(false);

  useEffect(() => {
    setOverridePrice(!!props.dataItem.price);
    setOverrideChargeSettings(
      !!(
        props.dataItem.chargeType ||
        props.dataItem.chargeCalculation ||
        props.dataItem.chargeAmount
      )
    );
  }, [props.dataItem.price]);

  const formatChargeAmount = (amount?: number | null, calculation?: ChargeCalculation | null) =>
    amount == null
      ? "—"
      : calculation === ChargeCalculation.Percentage
        ? `${amount}%`
        : formatPrice(Number(amount));

  const hasCustomPrice = props.dataItem.price != null;

  const hasCustomCharge = !!(props.dataItem.chargeType
    || props.dataItem.chargeCalculation
    || props.dataItem.chargeAmount != null);

  const defaultChargeText =
    `${props.service.defaultChargeType ?? "—"} · ${props.service.defaultChargeCalculation ?? "—"} · ${formatChargeAmount(props.service.defaultChargeAmount, props.service.defaultChargeCalculation)}`;

  const customChargeText =
    `${props.dataItem.chargeType ?? "—"} · ${props.dataItem.chargeCalculation ?? "—"} · ${formatChargeAmount(props.dataItem.chargeAmount, props.dataItem.chargeCalculation)}`;

  const validationSchema = yup.object().shape({
    price: yup.number().when("overridePrice", {
        is: true,
        then: (schema) =>
          schema
            .required("Price is required")
            .typeError("Price must be a number")
            .moreThan(0, "Price must be greater than 0"),
        otherwise: (schema) => schema.nullable(),
      }),
      chargeType: yup
        .mixed<ChargeType>()
        .nullable()
        .when("overrideChargeSettings", {
          is: true,
          then: (schema) =>
            schema
              .oneOf(Object.values(ChargeType), "Please select a valid charge type")
              .required("Charge type is required"),
          otherwise: (schema) => schema.nullable(),
        }),
      chargeCalculation: yup
        .mixed<ChargeCalculation>()
        .nullable()
        .when("overrideChargeSettings", {
          is: true,
          then: (schema) =>
            schema
              .oneOf(Object.values(ChargeCalculation), "Please select a valid charge calculation")
              .required("Charge calculation is required"),
          otherwise: (schema) => schema.nullable(),
        }),
      chargeAmount: yup
        .number()
        .nullable()
        .when("overrideChargeSettings", {
          is: true,
          then: (schema) =>
            schema
              .typeError("Charge Amount must be a number")
              .required("Charge Amount is required")
              .min(0, "Charge amount cannot be less than 0"),
          otherwise: (schema) => schema.nullable(),
        }),
      serviceId: yup.number().required("Service is required"),
  });

  return (
    <SlideOutModal
      {...props}
      title={`${props.dataItem.location?.name}`}
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button
                onClick={() => props.showState.setter(false)}
                variant="cancel"
                className="w-100"
              >
                Cancel
              </Button>
            </div>
            {managePricing && (
              <div className="col-12 ps-2">
                <Button
                  onClick={() => {
                    submitRef.current?.click();
                  }}
                  variant="primary"
                  className="w-100"
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>
      }
    >
      <Formik<EditServiceFormValues>
        validationSchema={validationSchema}
        initialValues={{
          overridePrice: overridePrice,
          overrideChargeSettings: overrideChargeSettings,
          price: props.dataItem.price,
          chargeType: props.dataItem.chargeType || null,
          chargeCalculation: props.dataItem.chargeCalculation || null,
          chargeAmount: props.dataItem.chargeAmount,
          serviceId: props.dataItem.serviceId,
        }}
        onSubmit={(values) => {
          props.onSubmit({
            ...props.dataItem,
            ...values,
            chargeType: values.chargeType,
            chargeCalculation: values.chargeCalculation,
            chargeAmount: values.chargeAmount,
          } as ServiceLocationModel);
          props.showState.setter(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          handleChange,
          setFieldValue,
          setTouched,
          setErrors,
        }) => {
          const handleChargeAmountChange = (e: any) => {
            const value = e.target.value;
            setFieldValue("chargeAmount", value === "" ? null : value);
          };
          return (
            <Form onSubmit={handleSubmit} className="px-2 pt-2">

              {/* Service defaults */}
              <FormSectionHeader>Service Defaults</FormSectionHeader>
              <Row className="mb-2">
                <Col xs={12}>
                  <Form.Label className="small text-body-secondary mb-1">Default Price</Form.Label>
                  <Form.Control
                    plaintext
                    readOnly
                    className="fw-semibold p-0"
                    value={formatPrice(Number(props.service.defaultPrice ?? 0))}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label className="small text-body-secondary mb-1">Default Charge</Form.Label>
                  <Form.Control
                    plaintext
                    readOnly
                    className="fw-semibold p-0"
                    value={defaultChargeText}
                  />
                </Col>
              </Row>

              <FormSectionDivider />

              {managePricing ? (
              <>
              <FormSectionHeader>Location Overrides</FormSectionHeader>

              <div className="d-flex align-items-center justify-content-between mb-2">
                <FormToggleLabel
                  className="me-3"
                  title="Custom Price"
                  description="Charge a different price for this service at this location"
                />
                <div className="form-check form-switch fs-4 flex-shrink-0 ps-0">
                  <input
                    className="form-check-input ms-0 mt-0"
                    type="checkbox"
                    role="switch"
                    style={{ cursor: "pointer" }}
                    checked={overridePrice}
                    onChange={() => {
                      const newValue = !overridePrice;
                      setOverridePrice(newValue);
                      setFieldValue("overridePrice", newValue);

                      if (!newValue) {
                        setFieldValue("price", null);
                        setFieldValue("chargeAmount", null);
                        setFieldValue("chargeCalculation", null);
                        setFieldValue("chargeType", null);
                      }

                      setTouched({
                        price: false,
                        chargeAmount: false,
                        chargeCalculation: false,
                        chargeType: false,
                      });
                      setErrors({
                        price: undefined,
                        chargeAmount: undefined,
                        chargeCalculation: undefined,
                        chargeType: undefined,
                      });
                    }}
                  />
                </div>
              </div>

              {overridePrice && (
                <div style={{ animation: 'slideDown 0.2s ease' }} className="mt-3">
                  <Row>
                    <Form.Group controlId="price">
                      <FloatingLabel label="Price" className="mb-3">
                        <Form.Control
                          type="number"
                          name="price"
                          placeholder="Price"
                          value={values.price!}
                          onChange={handleChange}
                          isInvalid={touched.price && !!errors.price}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.price}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Row>
                </div>
              )}

              <FormSectionDivider subtle />

              <div className="d-flex align-items-center justify-content-between mb-2">
                <FormToggleLabel
                  className="me-3"
                  title="Custom Charge Configuration"
                  description="Override how customers are charged at this location"
                />
                <div className="form-check form-switch fs-4 flex-shrink-0 ps-0">
                  <input
                    className="form-check-input ms-0 mt-0"
                    type="checkbox"
                    role="switch"
                    style={{ cursor: "pointer" }}
                    checked={overrideChargeSettings}
                    onChange={() => {
                      const newValue = !overrideChargeSettings;
                      setOverrideChargeSettings(newValue);
                      setFieldValue("overrideChargeSettings", newValue);

                      if (!newValue) {
                        setFieldValue("chargeAmount", null);
                        setFieldValue("chargeCalculation", null);
                        setFieldValue("chargeType", null);
                      }

                      setTouched({
                        chargeAmount: false,
                        chargeCalculation: false,
                        chargeType: false,
                      });
                      setErrors({
                        chargeAmount: undefined,
                        chargeCalculation: undefined,
                        chargeType: undefined,
                      });
                    }}
                  />
                </div>
              </div>

              {overrideChargeSettings && (
                <div style={{ animation: 'slideDown 0.2s ease' }} className="mt-3">
                  <Row>
                    <Col>
                      <Form.Group controlId="chargeType">
                        <SearchableChargeTypeDropdown
                          defaultItem={true}
                          name="chargeType"
                          value={values.chargeType}
                          placeholder="Charge Type"
                          onChange={(e) => {
                            if (e.target.value.value !== null) {
                              setTouched({ chargeAmount: false });
                              setErrors({ chargeAmount: undefined });
                            }

                            if (e.target.value.value === null) {
                              setFieldValue("chargeCalculation", null);
                              setFieldValue("chargeAmount", null);
                            }
                            HandleDropDownListChange(e, setFieldValue);
                          }}
                          isInvalid={touched.chargeType && !!errors.chargeType}
                        >
                          <Form.Control.Feedback type="invalid">
                            {errors.chargeType}
                          </Form.Control.Feedback>
                        </SearchableChargeTypeDropdown>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Form.Group controlId="chargeCalculation">
                        <SearchableChargeCalcDropdown
                          defaultItem={true}
                          name="chargeCalculation"
                          value={values.chargeCalculation}
                          onChange={(e) =>
                            HandleDropDownListChange(e, setFieldValue)
                          }
                          isInvalid={
                            touched.chargeCalculation &&
                            !!errors.chargeCalculation
                          }
                        >
                          <Form.Control.Feedback type="invalid">
                            {errors.chargeCalculation}
                          </Form.Control.Feedback>
                        </SearchableChargeCalcDropdown>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Form.Group controlId="chargeAmount">
                      <FloatingLabel
                        label={`Charge Amount${values.chargeCalculation === ChargeCalculation.Fixed ? ' ($)' : values.chargeCalculation === ChargeCalculation.Percentage ? ' (%)' : '' }`}
                        className="mb-3"
                      >
                        <Form.Control
                          type="number"
                          name="chargeAmount"
                          placeholder="Charge Amount"
                          value={values.chargeAmount ?? ""}
                          onChange={handleChargeAmountChange}
                          isInvalid={
                            touched.chargeAmount && !!errors.chargeAmount
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.chargeAmount}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Row>
                </div>
              )}
              </>
              ) : (
              <>
              <FormSectionHeader>Pricing At This Location</FormSectionHeader>
              {hasCustomPrice || hasCustomCharge ? (
                <Row className="mb-2">
                  <Col xs={12}>
                    <Form.Label className="small text-body-secondary mb-1">Price</Form.Label>
                    <Form.Control
                      plaintext
                      readOnly
                      className="fw-semibold p-0"
                      value={hasCustomPrice
                        ? `${formatPrice(Number(props.dataItem.price))} (custom)`
                        : "Service default"}
                    />
                  </Col>
                  <Col xs={12}>
                    <Form.Label className="small text-body-secondary mb-1">Charge Configuration</Form.Label>
                    <Form.Control
                      plaintext
                      readOnly
                      className="fw-semibold p-0"
                      value={hasCustomCharge ? `${customChargeText} (custom)` : "Service default"}
                    />
                  </Col>
                </Row>
              ) : (
                <p className="small text-body-secondary mb-0">
                  This location uses the service's default pricing.
                </p>
              )}
              </>
              )}

              <BSButton ref={submitRef} type="submit" className="d-none" />
            </Form>
          );
        }}
      </Formik>
    </SlideOutModal>
  );
}

export default EditServiceLocationModal;
